import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface PageHeroProps {
  tag: string;
  title: string;
  titleAccent?: string;
  subtitle?: string;
  breadcrumb: string;
  bgImage?: string;
}

const PageHero = ({ tag, title, titleAccent, subtitle, breadcrumb, bgImage }: PageHeroProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative pt-28 pb-16 md:pt-40 md:pb-24 bg-black overflow-hidden">
      {bgImage && (
        <>
          <img
            src={bgImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top opacity-10"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black pointer-events-none" />
        </>
      )}

      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-yellow-400/20 to-transparent" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-yellow-400/3 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div
          className={`flex items-center gap-2 mb-7 text-xs text-zinc-600 uppercase tracking-widest transition-all duration-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`}
        >
          <Link to="/" className="hover:text-yellow-400 transition-colors cursor-pointer whitespace-nowrap">Inicio</Link>
          <i className="ri-arrow-right-s-line text-zinc-700" />
          <span className="text-zinc-500 whitespace-nowrap">{breadcrumb}</span>
        </div>

        <div
          className={`transition-all duration-600 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="w-6 h-px bg-yellow-400" />
            <span className="text-yellow-400 text-xs font-bold uppercase tracking-[0.25em]">{tag}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase text-white leading-none tracking-wide">
            {title}
            {titleAccent && (
              <span className="text-yellow-400"> {titleAccent}</span>
            )}
          </h1>
          {subtitle && (
            <p className="text-zinc-400 text-sm mt-5 max-w-xl leading-relaxed font-light">{subtitle}</p>
          )}
        </div>

        <div
          className={`mt-8 h-px bg-gradient-to-r from-yellow-400/50 via-yellow-400/20 to-transparent transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 w-full' : 'opacity-0 w-0'
          }`}
        />
      </div>
    </section>
  );
};

export default PageHero;
